import { IReceiptState } from "@/interfaces/store/IReceipt";
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "..";
import POSConfigModule from "./pos-config";
import { CounterStatus, ICounter } from "@/types/db/transaction";
import { formatCounter } from "@smeetz/counter-formatter";
import {
  getReceiptCounter,
  postReceiptCounter,
  patchToIncrementReceiptCounter,
  patchExactReceiptCounter,
} from "@/api/receipt-counter/receipt-counter";
import { LogLevel, LogService } from "@/services/logging";
import DeviceModule from "./device";
import { dexieDB } from "@/services/dexie-database";
import { initiateReceiptCountersSync } from "@/services/sync-service";
import { SyncStatus } from "@/types/db/product-slot-booking";
import BookingModule from "./booking";

@Module({ dynamic: true, namespaced: true, store, name: "Receipt" })
class ReceiptStoreModule extends VuexModule implements IReceiptState {
  // holds all data related to counter logic
  // It represents only the current counter, which is attached to the last order sessio completion
  public receiptCounter: ICounter | null = null;

  /**
   * Whether the current receipt to print is a copy of another one or no
   * A Copy Receipt is:
   * 1- After doing transactions, user print not only one receipt but many receipts. The first printed receipt is original, but the rest are copies
   * 2- User pulling an order but does no edits and just go straight to completed-UI to print receipt again
   *
   * We identify the first case by having a flag receiptCounter.isCopy : true in case of user re-printing and reprinting
   * We identify the second case by having the newly completed orderSession having no accEntry linked to it
   *
   */
  public get isReceiptCopy() {
    // Check if the user has tried to print the same receipt again and again in within the same order session
    if (this.receiptCounter?.isCopy) {
      return true;
    }

    // Check if the current order session has accountingEntries linked to it
    if (BookingModule.isCurrentOrderSessionEmpty) {
      return true;
    }

    // So this receiptCounter object wasn't created by a user printing and printing again the same receipt
    // And this orderSession has transactions in it, so it is not empty
    return false;
  }

  @Mutation
  setReceiptCounter(remoteCounter: ICounter) {
    this.receiptCounter = remoteCounter;
  }

  @Mutation
  incrementReceiptCounterLocally() {
    if (!this.receiptCounter) {
      // We should never be in a block of code where we try to increment a counter that was not pulled yet
      return;
    }
    const incrementedValue = this.receiptCounter.value + 1;
    this.receiptCounter.valueReset = this.receiptCounter.value;
    this.receiptCounter.value = incrementedValue;
    this.receiptCounter.formattedValue = formatCounter({
      reference: this.receiptCounter.reference,
      value: this.receiptCounter.value,
    });
  }

  @Mutation
  setReceiptAsPrinted() {
    if (!this.receiptCounter) {
      return;
    }
    this.receiptCounter.isReceiptPrinted = true;
  }

  @Mutation
  setReceiptAsCopy() {
    if (!this.receiptCounter) {
      return;
    }
    this.receiptCounter.isCopy = true;
  }

  @Action
  async incrementReceiptCounter() {
    if (!this.receiptCounter) {
      // We should never be in a block of code where we try to increment a counter that was not pulled yet
      return;
    }
    const groupId = String(POSConfigModule.groupInfo?.id);
    const reference = this.receiptCounter.reference;
    try {
      const newReceiptCounter = await patchToIncrementReceiptCounter({
        groupId: groupId,
        reference: reference,
      });
      this.setReceiptCounter(newReceiptCounter);
    } catch (err: any) {
      LogService.info(
        `Receipt Counter: error when trying to PATCH increment receipt counter for groupId: ${groupId} and reference: ${reference}`,
        {
          err,
        }
      );

      // TODO HAMZA: This should be inserted somewhere to keep retrying
    }
  }

  @Mutation
  public restore(moduleFields: IReceiptState) {
    const { receiptCounter } = moduleFields;
    if (receiptCounter) {
      this.receiptCounter = receiptCounter;
    }
  }

  @Action
  public async fetchReceiptCounter(deviceUid: string): Promise<ICounter> {
    const groupId = String(POSConfigModule.groupInfo?.id);
    const reference =
      "P" + String(DeviceModule.hardwareLocation?.deviceUid).substring(0, 3);
    try {
      const receiptCounter: ICounter = await getReceiptCounter({
        groupId: groupId,
        reference: reference,
      });
      return receiptCounter;
    } catch (err: any) {
      LogService.info(
        `Receipt Counter: error when trying to GET receipt counter for groupId: ${groupId} and reference: ${reference}`,
        {
          err,
        }
      );
      // Check for 404 error
      if (err.response && err.response.status === 404) {
        // Call postReceiptCounter if 404
        // This will trigger backed to create the first counter for this device
        try {
          const initiatedReceiptCounter = await postReceiptCounter({
            groupId,
            reference,
          });
          // Lets return the hard coded counter for now
          return initiatedReceiptCounter;
        } catch (error) {
          LogService.info(
            `Receipt Counter: error when trying to POST receipt counter for groupId: ${groupId} and reference: ${reference} to trigger the first counter creation`,
            {
              err,
            }
          );
          throw new Error(
            "Receipt Counter: could not get receipt counter, nor initialize it"
          );
        }
      } else {
        throw new Error("Receipt Counter: could not GET receipt counter");
      }
    }
  }

  @Action
  async manuallyIncrementAndPatch() {
    if (!this.receiptCounter) {
      // We should never be in a block of code where we try to increment a counter that was not pulled yet
      return;
    }
    const groupId = String(POSConfigModule.groupInfo?.id);
    const reference = this.receiptCounter.reference;
    try {
      // Lets first increment the receipt counter locally
      this.incrementReceiptCounterLocally();

      // Now lets PATCH remote receipt counter to set an exact value
      const newReceiptCounter = await patchExactReceiptCounter({
        groupId: groupId,
        reference: reference,
        value: this.receiptCounter.value,
        status: CounterStatus.Active,
      });
      this.setReceiptCounter(newReceiptCounter);
    } catch (err: any) {
      LogService.info(
        `Receipt Counter: error when trying to PATCH exact value for receipt counter for groupId: ${groupId} and reference: ${reference} with value: ${this.receiptCounter.value}`,
        { err },
        LogLevel.Error
      );

      // Store the failed payload in Dexie with syncStatus=Pending for retry
      try {
        await dexieDB.receiptCounterPayload.add({
          _id: this.receiptCounter.formattedValue,
          groupId,
          reference,
          value: this.receiptCounter.value,
          status: CounterStatus.Active,
          syncStatus: SyncStatus.Pending,
        });

        // Trigger the sync job to retry
        initiateReceiptCountersSync();
      } catch (dexieErr) {
        LogService.info(
          `Receipt Counter: error storing failed PATCH payload into Dexie for groupId: ${groupId} and reference: ${reference} with value: ${this.receiptCounter.value}`,
          { err: dexieErr },
          LogLevel.Error
        );
      }
    }
  }
}

const ReceiptModule = getModule(ReceiptStoreModule);
export default ReceiptModule;
